import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityRounded from "@mui/icons-material/VisibilityRounded";
import NotificationsActiveRounded from "@mui/icons-material/NotificationsActiveRounded";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import DashboardLayout from "../../../layoutcontainers/DashboardLayout";
import MaterialButton from "../../../components/MaterialButton";
import {
  GetNotificationById,
  GetUserNotifications,
} from "../manageComplaints/data";
import BASEURL from "../../../pages/Utils/Baseurl";
import request from "graphql-request";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { useAuthData } from "../../context/loginContext/LoginContextProvider";
import "./Notification.css";
import "./Notification.css";

const GetAllNotifications = `
  query {
    getAllNotification {
      data {
        id
        user {
          id
          employeeName
        }
        module
        message
        status
        createdAt
      }
    }
  }
`;

const Notification = () => {
  const { userId } = useAuthData();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [viewNotificationData, setViewNotificationData] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notificationView, setNotificationView] = useState(false);
  const [activeFilter, setActiveFilter] = useState("all");

  const itemsPerPage = 5;
  const unreadCount = notifications.filter(
    (notification) => notification.status === "unread",
  ).length;
  const filteredNotifications =
    activeFilter === "unread"
      ? notifications.filter((notification) => notification.status === "unread")
      : notifications;
  const pageCount = Math.ceil(filteredNotifications.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const currentLogs = filteredNotifications.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  useEffect(() => {
    fetchNotificationData();
  }, []);

  //pagination function
  const handlePageChange = (direction) => {
    if (
      direction === "older" &&
      startIndex + itemsPerPage < filteredNotifications.length
    ) {
      setCurrentPage((prev) => prev + 1);
    }
    if (direction === "newer" && currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  //fetch api call
  const fetchNotificationData = async () => {
    setIsLoading(true);
    try {
      let allNotifications;
      const userDetails = JSON.parse(
        localStorage.getItem("userDetails") || "{}",
      );
      const isAdmin =
        userDetails?.role?.role?.toLowerCase() === "admin";

      if (isAdmin) {
        const res = await request(BASEURL, GetAllNotifications);
        allNotifications = res?.getAllNotification?.data || [];
      } else {
        const res = await request(BASEURL, GetUserNotifications, { userId });
        allNotifications = res?.getNotificationByUserid?.data || [];
      }

      // Sort unread notifications to the top
      const sortedNotifications = allNotifications.sort((a, b) =>
        a.status === "unread" && b.status !== "unread" ? -1 : 1
      );
      setNotifications(sortedNotifications);
      setCurrentPage(0);
    } catch (error) {
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  };

  //view api call
  const viewNotification = async (id) => {
    try {
      const res = await request(BASEURL, GetNotificationById, { id });
      const notificationData = res?.getNotificationById?.data[0] || null;
      setViewNotificationData(notificationData);
      setDialogOpen(true);
      setNotificationView(true)
    } catch (error) {
      setViewNotificationData(null);
    }
  };

  

  return (
    <DashboardLayout notificationView={notificationView}>
      <Box className="notification-page">
        <header className="notification-header">
          <div className="notification-heading">
            <p>Updates from across your workspace.</p>
          </div>
          <div className="notification-header-actions">
            <div className="notification-unread-count">
              <NotificationsActiveRounded />
              <span style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                <strong>{unreadCount}</strong>
                <small>unread</small>
              </span>
            </div>
            <IconButton
              className="notification-refresh"
              onClick={fetchNotificationData}
              disabled={isLoading}
              aria-label="Refresh notifications"
              title="Refresh notifications"
            >
              <RefreshRounded />
            </IconButton>
          </div>
        </header>

        <div className="notification-toolbar">
          <div className="notification-tabs" role="tablist" aria-label="Notification filter">
            <button
              className={`notification-tab${activeFilter === "all" ? " is-active" : ""}`}
              type="button"
              role="tab"
              aria-selected={activeFilter === "all"}
              onClick={() => {
                setActiveFilter("all");
                setCurrentPage(0);
              }}
            >
              All <span>{notifications.length}</span>
            </button>
            <button
              className={`notification-tab${activeFilter === "unread" ? " is-active" : ""}`}
              type="button"
              role="tab"
              aria-selected={activeFilter === "unread"}
              onClick={() => {
                setActiveFilter("unread");
                setCurrentPage(0);
              }}
            >
              Unread <span>{unreadCount}</span>
            </button>
          </div>
          <span className="notification-range">
            {filteredNotifications.length
              ? `${startIndex + 1}-${Math.min(
                  startIndex + itemsPerPage,
                  filteredNotifications.length,
                )} of ${filteredNotifications.length}`
              : "No activity"}
          </span>
        </div>

        <section className="notification-feed" aria-label="Notifications list">
          {isLoading ? (
            <div className="notification-state" role="status">
              <CircularProgress size={24} />
              <span>Loading notifications</span>
            </div>
          ) : currentLogs.length ? (
            currentLogs.map((log) => {
              const isUnread = log.status === "unread";
              const date = new Date(log.createdAt);

              return (
                <article
                  className={`notification-entry${isUnread ? " is-unread" : ""}`}
                  key={log.id}
                >
                  <div className="notification-entry__rail" aria-hidden="true">
                    <span className="notification-entry__icon">
                      {isUnread ? (
                        <NotificationsActiveRounded />
                      ) : (
                        <CheckCircleIcon />
                      )}
                    </span>
                  </div>
                  <div className="notification-entry__body">
                    <div className="notification-entry__meta">
                      <span className="notification-module">
                        {log.module || "Update"}
                      </span>
                      {isUnread && <span className="notification-new">NEW</span>}
                      {log.user?.employeeName && (
                        <span className="notification-actor">
                          From {log.user.employeeName}
                        </span>
                      )}
                    </div>
                    <p className="notification-entry__message">{log.message}</p>
                  </div>
                  <div className="notification-entry__aside">
                    <time dateTime={date.toISOString()}>
                      <span>
                        {date.toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                      <small>
                        {date.toLocaleTimeString(undefined, {
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </small>
                    </time>
                    <IconButton
                      className="notification-view-button"
                      size="small"
                      aria-label="View notification details"
                      onClick={() => viewNotification(log.id)}
                    >
                      <VisibilityRounded fontSize="small" />
                    </IconButton>
                  </div>
                </article>
              );
            })
          ) : (
            <div className="notification-state notification-state--empty">
              <span className="notification-state__icon">
                <NotificationsActiveRounded />
              </span>
              <strong>
                {activeFilter === "unread"
                  ? "You’re all caught up"
                  : "No notifications yet"}
              </strong>
              <span>
                {activeFilter === "unread"
                  ? "There are no unread updates right now."
                  : "New workspace activity will appear here."}
              </span>
            </div>
          )}
        </section>

        <footer className="notification-pagination">
          <span>
            Page {pageCount ? currentPage + 1 : 0} of {pageCount}
          </span>
          <div>
            <IconButton
              onClick={() => handlePageChange("newer")}
              disabled={currentPage === 0 || isLoading}
              aria-label="Previous page"
            >
              <ChevronLeftRounded />
            </IconButton>
            <IconButton
              onClick={() => handlePageChange("older")}
              disabled={startIndex + itemsPerPage >= filteredNotifications.length || isLoading}
              aria-label="Next page"
            >
              <ChevronRightRounded />
            </IconButton>
          </div>
        </footer>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            <b>Notification Details</b>
          </DialogTitle>
          <DialogContent>
            {viewNotificationData ? (
              <>
                <Typography variant="body2">
                  <strong>Module:</strong> {viewNotificationData.module}
                </Typography>
                <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                  <strong>Message:</strong> {viewNotificationData.message}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {viewNotificationData.status}
                </Typography>
                <Typography variant="body2">
                  <strong>Date:</strong>{" "}
                  {new Date(viewNotificationData.createdAt).toLocaleString()}
                </Typography>
              </>
            ) : (
              <Typography variant="body1" color="error">
                Unable to fetch details.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <MaterialButton
              variant="gradient"
              color="dark"
              sx={{ borderRadius: "20px" }}
              onClick={() => {
                setDialogOpen(false);
                fetchNotificationData();
              }}
            >
              Close
            </MaterialButton>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
};

export default Notification;
